<script setup lang="ts">
import type { ExperimentBranchConfig } from '../../model/settings/types'

import { AppTable, AppTh, AppTr, AppTd } from '@shared/ui/tables'
import { CardAdmin } from '@shared/ui/cards'

import { useSettingsStore } from '../../model/settings/store'

interface ExperimentBranchRow {
  name: string
  percentage: number
}

interface ExperimentRow {
  name: string
  branches: ExperimentBranchRow[]
  overrideBranch: string | null
  startDate: string | null
  isClosed: boolean
}

defineOptions({
  name: 'TableExperiments',
})

const settingsStore = useSettingsStore()

const toPercentage = (branch: ExperimentBranchConfig): number => {
  if (typeof branch === 'number') {
    return branch
  }

  return branch?.percentage ?? 0
}

const rows = computed<ExperimentRow[]>(() =>
  Object.entries(settingsStore.getExperiments).map(([name, config]) => ({
    name,
    branches: Object.entries(config.branches ?? {}).map(([branchName, branchConfig]) => ({
      name: branchName,
      percentage: toPercentage(branchConfig),
    })),
    overrideBranch: config.override_branch ?? null,
    startDate: config.start_date ?? null,
    isClosed: Boolean(config.override_branch),
  })),
)
</script>

<template>
  <CardAdmin
    title="Experiments"
    fullwidth
  >
    <AppTable
      class="rounded-table"
      :loading="settingsStore.isLoading"
      :paginated="false"
    >
      <template #head>
        <AppTr>
          <AppTh>Name</AppTh>
          <AppTh>Branches</AppTh>
          <AppTh>Override</AppTh>
          <AppTh>Start date</AppTh>
          <AppTh>Status</AppTh>
        </AppTr>
      </template>
      <template #body>
        <AppTr
          v-for="row in rows"
          :key="row.name"
        >
          <AppTd>{{ row.name }}</AppTd>
          <AppTd>
            <div
              v-for="branch in row.branches"
              :key="branch.name"
              class="table-experiments__branch"
            >
              <span>{{ branch.name }}</span>
              <span class="table-experiments__branch-percentage">{{ branch.percentage }}%</span>
            </div>
            <span v-if="!row.branches.length">—</span>
          </AppTd>
          <AppTd>{{ row.overrideBranch ?? '—' }}</AppTd>
          <AppTd>{{ row.startDate ?? '—' }}</AppTd>
          <AppTd>
            <span :class="row.isClosed ? 'table-experiments__status--closed' : 'table-experiments__status--active'">
              {{ row.isClosed ? 'Closed' : 'Active' }}
            </span>
          </AppTd>
        </AppTr>
        <AppTr v-if="!rows.length">
          <AppTd>No experiments configured.</AppTd>
          <AppTd />
          <AppTd />
          <AppTd />
          <AppTd />
        </AppTr>
      </template>
    </AppTable>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./TableExperiments.scss" />
